import React, { useState, useEffect } from "react";
import styled from "styled-components";
import API from "../../../util/api";
import ConfirmationModal from "../../Common/ConfirmationModal";
import { actionContainerStyles, actionLinkStyles, actionHeaderStyles } from "../common/ActionStyles";
import ResponsiveActions from "../common/ResponsiveActions";
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";

const LINK_HOVER_COLOR = "#F7941D";

const Container = styled.div`
  padding: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: stretch;
`;

const Actions = styled.div`
  ${actionContainerStyles};
  // border: 1px solid ${({ colors }) => colors.border};

  h3 {
    ${actionHeaderStyles};
    border-bottom: 1px solid ${({ colors }) => colors.border};
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 12px 16px;
  }

  li + li {
    margin-top: 4px;
  }

  a {
    ${actionLinkStyles};
    padding-left: 16px;
    color: #2C4960;

    &:hover {
      color: ${LINK_HOVER_COLOR};
    }
  }
`;

const View = styled.div`
  flex: 1 1 0;
  min-width: 0;
  width: 100%;
`;

const Table = styled.table`
  ${adminTable};
  font-size: 14px;

  thead th {
    ${adminTableHeaderCell};
  }

  tbody td {
    ${adminTableDataCell};
  }
`;

const LinkButton = styled.a`
  display: inline-block;
  padding: 4px 8px;
  background: ${({ colors }) => colors.buttonBackground};
  color: white;
  border-radius: 4px;
  font-size: 12px;
  text-decoration: none;
  cursor: pointer;
  &:hover {
    background: ${({ colors }) => colors.buttonHoverBackground};
  }
`;

const Form = styled.form`
  background: ${({ colors }) => colors.cardBackground};
  padding: 20px;
  border-radius: 5px;
`;

const Field = styled.div`
  margin-bottom: 1em;
  label {
    display: block;
    margin-bottom: 0.3em;
    color: ${({ colors }) => colors.labelText};
    font-size: 14px;
  }
  select {
    width: 100%;
    padding: 8px;
    border: 1px solid ${({ colors }) => colors.border};
    border-radius: 4px;
    font-size: 14px;
  }
`;

const SubmitRow = styled.div`
  margin-top: 1.5em;
  input[type="submit"] {
    padding: 8px 16px;
    background: ${({ colors }) => colors.buttonBackground};
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    &:hover {
      background: ${({ colors }) => colors.buttonHoverBackground};
    }
  }
`;

export default function SalesRegionsContent({ colors, onTabChange }) {
  const [loading, setLoading] = useState(true);
  const [regions, setRegions] = useState([]);
  const [salesOptions, setSalesOptions] = useState([]);
  const [view, setView] = useState("list"); // 'list' or 'edit'
  const [selected, setSelected] = useState(null);

  // confirmation modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [modalStatus, setModalStatus] = useState("Success");

  // fetch regions & salespersons
  useEffect(() => {
    async function loadAll() {
      setLoading(true);
      try {
        const r = await API.getSalesRegion({});
        const regs = r.result || [];
        const s = await API.getSalesPerson({});
        const persons = s.result || [];
        const map = {};
        persons.forEach(u => {
          if (!map[u.sp_code]) {
            map[u.sp_code] = {
              code: u.sp_code,
              name:
                u.first_name || u.last_name
                  ? `${u.first_name || ""} ${u.last_name || ""}`.trim()
                  : u.sp_code,
            };
          }
        });
        setSalesOptions(Object.values(map));
        setRegions(regs);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    loadAll();
  }, []);

  const handleEditClick = (region) => {
    setSelected({ ...region });
    setView("edit");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setConfirmOpen(false);
    setModalStatus("Success");
    try {
      const resp = await API.updateSalesRegion({
        sp_code: selected.sp_code,
        state_code: selected.state,
      });
      setRegions(prev =>
        prev.map(r =>
          r.state === selected.state ? { ...r, sp_code: selected.sp_code } : r
        )
      );
      const status = resp?.status === "Failed" ? "Failed" : "Success";
      setModalTitle(status === "Failed" ? "Error" : "Success");
      setModalMessage(
        resp?.message ||
        (status === "Failed" ? "Failed to save changes." : "Sales region updated.")
      );
      setModalStatus(status);
    } catch (err) {
      console.error(err);
      setModalTitle("Error");
      setModalMessage("Failed to save changes.");
      setModalStatus("Failed");
    } finally {
      setResultOpen(true);
    }
  };

  const handleResultClose = () => {
    setResultOpen(false);
    setModalStatus("Success");
  };

  if (loading) return <p>Loading…</p>;

  return (
    <Container>
      <ConfirmationModal
        show={confirmOpen}
        title="Confirm"
        message="Save changes to this region?"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleSubmit}
      />
      <ConfirmationModal
        show={resultOpen}
        title={modalTitle}
        message={modalMessage}
        onCancel={handleResultClose}
        onConfirm={handleResultClose}
        status={modalStatus}
      />

      <ResponsiveActions
        colors={colors}
        renderActions={() => (
          <Actions colors={colors}>
            <h3>Actions</h3>
            <ul>
              {view === "list" ? (
                <li>
                  <a onClick={() => onTabChange("Salespersons")}>
                    View Sales People
                  </a>
                </li>
              ) : (
                <li>
                  <a
                    onClick={() => {
                      setView("list");
                      setSelected(null);
                    }}
                  >
                    Cancel
                  </a>
                </li>
              )}
            </ul>
          </Actions>
        )}
      />

      <View>
        {view === "list" ? (
          <Table>
            <thead>
              <tr>
                <th>State</th>
                <th>State Name</th>
                <th>Sales Person</th>
                <th>Sales Person Name</th>
                <th>&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {regions.map(r => (
                <tr key={r.state}>
                  <td>{r.state}</td>
                  <td>{r.state_name}</td>
                  <td>{r.sp_code}</td>
                  <td>
                    {r.first_name || r.last_name
                      ? `${r.first_name || ""} ${r.last_name || ""}`.trim()
                      : ""}
                  </td>
                  <td>
                    <LinkButton
                      colors={colors}
                      onClick={() => handleEditClick(r)}
                    >
                      Edit
                    </LinkButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <>
            <h1 style={{ color: colors.labelText }}>
              Editing Region: {selected.state}
            </h1>
            <Form colors={colors} onSubmit={e => { e.preventDefault(); setConfirmOpen(true); }}>
              <Table>
                <thead>
                  <tr>
                    <th>State</th>
                    <th>State Name</th>
                    <th>Sales Person</th>
                    <th>&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{selected.state}</td>
                    <td>{selected.state_name}</td>
                    <td>
                      <Field colors={colors}>
                        <select
                          value={selected.sp_code}
                          onChange={e =>
                            setSelected(s => ({
                              ...s,
                              sp_code: e.target.value,
                            }))
                          }
                        >
                          {salesOptions.map(o => (
                            <option key={o.code} value={o.code}>
                              {o.code}
                            </option>
                          ))}
                        </select>
                      </Field>
                    </td>
                    <td>&nbsp;</td>
                  </tr>
                </tbody>
              </Table>
              <SubmitRow colors={colors}>
                <input type="submit" value="Submit Changes" />
              </SubmitRow>
            </Form>
          </>
        )}
      </View>

      <div style={{ clear: "both" }} />
    </Container>
  );
}
